import { Fragment } from 'react'
import { IconPaperclip } from '@tabler/icons-react'
import type { Communication, Practitioner } from '@medplum/fhirtypes'
import { formatDateTime } from '@/utils'
import { cn } from '@/utils/cn'
import type { PractitionerThreadOption, ThreadMessage } from '../service'
import { formatDateSeparator, getPractitionerName, messageDayKey } from '../presentation'
import { resolveReceiptStatus } from '../receipts'
import type { PendingMessage } from '../useThreadMessageSender'
import { ParticipantAvatar } from './ParticipantAvatar'
import { MessageReceipt } from './MessageReceipt'

type MessageListProps = {
  messages: ThreadMessage[]
  currentPractitionerId?: string
  /** Thread header, read for the other participants' read state. */
  thread?: Communication
  otherParticipants?: PractitionerThreadOption[]
  /** Optimistic bubbles for sends that have not been confirmed yet. */
  pending?: PendingMessage[]
  /** Own profile, so optimistic bubbles can show the right avatar and name. */
  currentPractitioner?: Practitioner
  onRetry?: (localId: string) => void
}

export const MessageList = ({
  messages,
  currentPractitionerId,
  thread,
  otherParticipants = [],
  pending = [],
  currentPractitioner,
  onRetry,
}: MessageListProps) => {
  let previousDayKey = ''

  return (
    <div className="flex flex-col gap-3">
      {messages.map(({ message, sender }) => {
        const isMine = Boolean(sender?.id && sender.id === currentPractitionerId)
        const contentString = message.payload?.[0]?.contentString
        const attachment = message.payload?.[0]?.contentAttachment

        const dayKey = messageDayKey(message.sent)
        const showSeparator = dayKey !== previousDayKey
        previousDayKey = dayKey

        return (
          <Fragment key={message.id}>
            {showSeparator && (
              <div className="flex items-center gap-3 py-1" role="separator">
                <span className="h-px flex-1 bg-[#E4E7EB]" />
                <span className="text-xs font-semibold text-[#9AA0A8]">
                  {formatDateSeparator(message.sent)}
                </span>
                <span className="h-px flex-1 bg-[#E4E7EB]" />
              </div>
            )}

            <div className={cn(['flex items-end gap-2', isMine ? 'justify-end' : 'justify-start'])}>
              {!isMine && <ParticipantAvatar practitioner={sender} size={32} />}

              <div
                className={cn([
                  'max-w-[75%] rounded-2xl px-3.5 py-2.5',
                  isMine
                    ? 'rounded-br-sm bg-[#E5533D] text-white shadow-[0_1px_2px_rgba(203,71,31,.3)]'
                    : 'rounded-bl-sm border border-[#E7E9EC] bg-white text-[#1F2733]',
                ])}
              >
                {contentString && (
                  <p className="text-sm leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap">
                    {contentString}
                  </p>
                )}

                {attachment?.url && (
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    className={cn([
                      'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs underline',
                      contentString && 'mt-2',
                      isMine ? 'bg-white/15 text-white' : 'bg-[#F1F2F4] text-[#1F2733]',
                    ])}
                  >
                    <IconPaperclip size={12} />
                    {attachment.title || 'Open attachment'}
                  </a>
                )}

                {!contentString && !attachment?.url && (
                  <p className={cn(['text-sm', isMine ? 'text-white/80' : 'text-[#9AA0A8]'])}>
                    Empty message
                  </p>
                )}

                <p
                  className={cn([
                    'mt-1 flex items-center gap-1.5 text-[11px] font-medium',
                    isMine ? 'text-white/75' : 'text-[#9AA0A8]',
                  ])}
                >
                  <span>
                    {getPractitionerName(sender)}
                    {message.sent ? ` · ${formatDateTime(message.sent, '-', true)}` : ''}
                  </span>
                  {/* Receipts only ever appear on messages the current user sent. */}
                  {isMine && thread && (
                    <MessageReceipt
                      status={resolveReceiptStatus(message, thread, otherParticipants)}
                    />
                  )}
                </p>
              </div>

              {isMine && <ParticipantAvatar practitioner={sender} size={32} />}
            </div>
          </Fragment>
        )
      })}

      {/* Optimistic bubbles: everything already confirmed is above, so these
          always belong at the end of the thread. */}
      {pending.map((item) => (
        <div key={item.localId} className="flex items-end justify-end gap-2">
          <div
            className={cn([
              'max-w-[75%] rounded-2xl rounded-br-sm bg-[#E5533D] px-3.5 py-2.5 text-white',
              item.status === 'sending' ? 'opacity-70' : 'opacity-100',
            ])}
          >
            <p className="text-sm leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap">
              {item.text}
            </p>
            <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-white/75">
              <span>{getPractitionerName(currentPractitioner)}</span>
              <MessageReceipt
                status={item.status}
                onRetry={onRetry ? () => onRetry(item.localId) : undefined}
              />
            </p>
          </div>
          <ParticipantAvatar practitioner={currentPractitioner} size={32} />
        </div>
      ))}
    </div>
  )
}
